import { installedItems } from "@p0security/shared/install/installed";
import { AwsRdsIntegration } from "@p0security/shared/integrations/resources/aws-rds/types";
import { arnInfo } from "@p0security/shared/integrations/resources/aws/util";
import {
  installSql,
  installTf,
} from "@p0security/shared/integrations/resources/mysql/iac";
import { ValidationError } from "@p0security/shared/types/error";
import Paragraph from "antd/lib/typography/Paragraph";
import { doc, getDoc } from "firebase/firestore";
import { sortBy } from "lodash";

import { DB } from "../../../providers/FirestoreProvider";
import { MysqlInstaller } from "./types";

const awsRdsIamWriteInstructions: MysqlInstaller["iam-write"]["instructions"] =
  (context, id, item) => {
    const { config } = context;
    const thisVpc = "vpcId" in item.hosting ? item.hosting.vpcId : null;

    // If a connector is already installed on this VPC, use that connector's identifier
    const othersInstalled = Object.values(config["iam-write"]).find(
      (c) =>
        "vpcId" in c.hosting &&
        c.hosting.vpcId === thisVpc &&
        (c.state === "configure" || c.state === "installed")
    );
    const otherConnectorArn = othersInstalled?.hosting.connectorArn;
    const otherConnectorId = otherConnectorArn
      ? arnInfo(otherConnectorArn)?.name
      : undefined;

    // Otherwise, create the connector
    const { connectorArn } = item.hosting;

    const { name: dbId, resourceType: type } = arnInfo(
      "instanceArn" in item.hosting ? item.hosting.instanceArn : undefined
    );

    if (!dbId) {
      throw new ValidationError(
        "instanceArn",
        { item },
        "Invalid instance ARN"
      );
    }
    if (type !== "db" && type !== "cluster") {
      throw new ValidationError(
        "instanceArn",
        { item },
        "Not a database or a cluster"
      );
    }
    return {
      help: (
        <>
          To install P0 on this account, provision P0&apos;s access using
          Terraform:
        </>
      ),
      commands: {
        iac: [
          {
            command: installSql,
            header: (
              <div>
                <Paragraph>
                  First, configure the P0 connector&apos;s MySQL user.
                </Paragraph>
                <Paragraph>Run these SQL statements in the database:</Paragraph>
              </div>
            ),
          },
          {
            command: installTf(context, {
              connectorArn,
              connectorId: otherConnectorId,
              id: dbId,
              type,
            }),
            header: (
              <Paragraph>
                Then, add this to your Terraform and apply it:
              </Paragraph>
            ),
          },
        ],
      },
    };
  };

export const mysqlInstaller: MysqlInstaller = {
  "iam-write": {
    items: {
      hosting: {
        options: {
          "aws-rds": {
            items: {
              vpcId: {
                optionProvider: async ({ tenantId }) => {
                  const rds = await getDoc(
                    doc(DB, `o/${tenantId}/integrations/aws-rds`)
                  );
                  const data = rds?.data();
                  if (!data) return [];
                  return sortBy(
                    Object.entries(
                      installedItems("iam-write", data as AwsRdsIntegration)
                    ).map(([id, item]) => ({
                      id,
                      label: item?.label ? `${item.label} (${id})` : id,
                    })),
                    "label"
                  );
                },
              },
            },
          },
        },
      },
    },
    instructions: awsRdsIamWriteInstructions,
  },
};
